import { type FC, useState } from 'react';
import { Box, Flex, useBreakpointValue } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { usePDP } from '../../../contexts/pdp/usePDP';
import { Button, Modal, Rating, Text } from '../../../ui-components';
import { IconButton } from '../../../ui-components/button/icon-button';
import type { Photo } from '../../../bff-api/turn-to/model';

export interface GalleryProps {
    isOpen: boolean;
    onClose: () => void;
    rating: number;
    nickName: string;
    firstName: string;
    lastName: string;
    text: string;
    photoList: Photo[];
    selectedPhoto?: Photo;
}
export const Gallery: FC<GalleryProps> = ({
    isOpen,
    onClose,
    selectedPhoto,
    rating,
    nickName,
    firstName,
    lastName,
    text,
    photoList,
}) => {
    const { product } = usePDP();
    const initialIndex = photoList.indexOf(selectedPhoto!);

    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(initialIndex);
    const iconSize = useBreakpointValue<'sm' | 'md'>({ base: 'sm', lg: 'md' });

    if (!selectedPhoto || !product) {
        return null;
    }

    const formattedDate = new Intl.DateTimeFormat('default', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(new Date(selectedPhoto.dateCreated));

    const shopNow = (): void => {
        onClose();
        window.scrollTo(0, 0);
    };

    const onNext = (): void => {
        if (selectedPhotoIndex < photoList.length - 1) {
            setSelectedPhotoIndex(selectedPhotoIndex + 1);
        }
    };

    const onPrev = (): void => {
        if (selectedPhotoIndex > 0) {
            setSelectedPhotoIndex(selectedPhotoIndex - 1);
        }
    };

    return (
        <Modal id="photo-modal" isOpen={isOpen} onClose={onClose} variant="no-frame" modalOnly>
            <Flex
                background="slt-pepper.100"
                position="absolute"
                top="50%"
                left={{ base: '-28px', lg: '-48px' }}
                transform="translate(-50% 0)"
                borderRadius="50%"
                w={6}
                h={6}
                alignItems="center"
                justifyContent="center"
            >
                <IconButton
                    iconName="navigation-arrow-left"
                    id="left-arrow"
                    onClick={onPrev}
                    size={iconSize}
                />
            </Flex>
            <Flex
                background="slt-pepper.100"
                position="absolute"
                top="50%"
                right={{ base: '-28px', lg: '-48px' }}
                transform="translate(-50% 0)"
                borderRadius="50%"
                w={6}
                h={6}
                alignItems="center"
                justifyContent="center"
            >
                <IconButton
                    iconName="navigation-arrow-right"
                    id="left-arrow"
                    onClick={onNext}
                    size={iconSize}
                />
            </Flex>
            <Flex h="100%" flexWrap={{ base: 'wrap', lg: 'nowrap' }} gap={0}>
                <Box
                    h={{ base: 'unset', lg: '100%' }}
                    flex={{ base: 'auto', lg: 1 }}
                    transition="width .2s, height .2s"
                    width={{ base: '100%', lg: 'unset' }}
                >
                    <img
                        width="100%"
                        height="100%"
                        src={photoList[selectedPhotoIndex].normalUrl}
                        alt={selectedPhoto.caption}
                        loading="lazy"
                    />
                </Box>
                <Flex
                    w={{ base: '100%', lg: '256px' }}
                    flexWrap="wrap"
                    flexDir="column"
                    justifyContent="space-between"
                >
                    <Box>
                        <Box padding={4} borderBottom="1px" borderColor="slt-pepper.300" w="100%">
                            {!!selectedPhoto.caption && <Text>{selectedPhoto.caption}</Text>}
                            <Text size="small" color="slt-pepper.700">
                                {firstName
                                    ? `${firstName} ${lastName?.[0] ?? ''}, ${formattedDate}`
                                    : `${nickName ?? ''}, ${formattedDate}`}
                            </Text>
                        </Box>
                        <Box padding={4}>
                            <Rating initialRating={rating} disabled />
                            <Text margin="1rem  0 0 0" size="small">
                                {text}
                            </Text>
                        </Box>
                    </Box>
                    <Box padding={4} borderTop="1px" borderColor="slt-pepper.300">
                        <Flex gap={2} marginBottom={4}>
                            <Box w="68px" h="68px">
                                <img
                                    height="100%"
                                    width="100%"
                                    src={product?.imageGroups?.[0].images[0].link}
                                    alt={product?.name}
                                />
                            </Box>
                            <Box flex={1} alignItems={{ base: 'center', lg: 'flex-start' }}>
                                <Text size="small" style="bold">
                                    {product?.name}
                                </Text>
                            </Box>
                        </Flex>
                        <Button variant="primary-black" wide onClick={shopNow}>
                            <FormattedMessage id="gallery.submit" defaultMessage="Shop now" />
                        </Button>
                    </Box>
                </Flex>
            </Flex>
        </Modal>
    );
};
